import { useState, useEffect } from 'react';
import { getBinConfigurations, updateProjectStep5, getProject } from '../../services/api';
import CustomBinConfigurator from './CustomBinConfigurator';

const WASTE_MANAGEMENT_MEASURES = [
  { id: 'signage', title: 'Bin Signage', description: 'Clear signage on all bins indicating waste stream' },
  { id: 'education', title: 'Tenant Education', description: 'Information pack for residents on waste sorting' },
  { id: 'accessibility', title: 'Accessible Bin Storage', description: 'Easy access for all residents including disabled' },
  { id: 'ventilation', title: 'Ventilated Storage', description: 'Adequate ventilation in bin storage areas' },
  { id: 'drainage', title: 'Wash-down Facilities', description: 'Drainage and cleaning facilities for bin areas' },
  { id: 'lighting', title: 'Adequate Lighting', description: 'Sufficient lighting in waste storage areas' },
  { id: 'collection_access', title: 'Collection Vehicle Access', description: 'Clear access path for waste collection vehicles' },
  { id: 'hardstand', title: 'Hardstand Surface', description: 'Durable, cleanable floor surface in bin areas' },
  { id: 'containment', title: 'Spill Containment', description: 'Bunding or containment for liquid waste spills' },
  { id: 'pest_control', title: 'Pest Management', description: 'Measures to prevent pest attraction and infestation' },
  { id: 'bulky_waste', title: 'Bulky Waste Arrangements', description: 'Designated area or arrangements for bulky items' },
  { id: 'review', title: 'Regular Review', description: 'Periodic review of waste management effectiveness' }
];

// Helper function to format bin configuration display
const formatBinDisplay = (streamData) => {
  if (!streamData) return '0x 0L';
  
  // Check if this is a custom configuration with bin_breakdown
  if (streamData.bin_breakdown && streamData.bin_breakdown.length > 0) {
    return streamData.bin_breakdown
      .filter(b => b.count > 0)
      .map(b => `${b.count}x ${b.size}L`)
      .join(' + ') || '0x 0L';
  }
  
  // Standard configuration with single bin_size
  const bins = streamData.bins_needed || 0;
  const size = streamData.bin_size || 0;
  return `${bins}x ${size}L`;
};

// Configuration selection card component
const ConfigCard = ({ config, optionNumber, isSelected, onClick, label, thirdStreamLabel = "Organics" }) => (
  <div
    onClick={onClick}
    className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
      isSelected
        ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-600'
        : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
    }`}
  >
    {/* Selection indicator */}
    <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
      isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
    }`}>
      {isSelected && (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </div>

    <h4 className="font-bold text-gray-900 mb-2">{label || `Option ${optionNumber}`}</h4>
    <p className="text-sm text-gray-600 mb-3">{config.description}</p>

    <div className="space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-500">Total Bins:</span>
        <span className="font-semibold text-gray-900">{config.total_bins}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Storage Area:</span>
        <span className="font-semibold text-gray-900">{config.storage_area} m²</span>
      </div>
    </div>

    {/* Bin breakdown */}
    <div className="mt-3 pt-3 border-t border-gray-200">
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center">
          <p className="text-gray-500">General</p>
          <p className="font-semibold">{formatBinDisplay(config.streams.general_waste)}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-500">Recycling</p>
          <p className="font-semibold">{formatBinDisplay(config.streams.recycling)}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-500">{thirdStreamLabel}</p>
          <p className="font-semibold">{formatBinDisplay(config.streams.organics)}</p>
        </div>
      </div>
    </div>
  </div>
);

const Step5ManagementMeasures = ({ project, onComplete, onBack }) => {
  const [configurations, setConfigurations] = useState([]);
  const [residentialConfigurations, setResidentialConfigurations] = useState([]);
  const [commercialConfigurations, setCommercialConfigurations] = useState([]);
  const [isMixedUse, setIsMixedUse] = useState(false);
  
  // Mode selection: 'auto' or 'custom'
  const [residentialMode, setResidentialMode] = useState('auto');
  const [commercialMode, setCommercialMode] = useState('auto');
  const [singleMode, setSingleMode] = useState('auto');
  
  // Auto-mode selections
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedResidentialConfig, setSelectedResidentialConfig] = useState(null);
  const [selectedCommercialConfig, setSelectedCommercialConfig] = useState(null);
  
  // Custom-mode configurations
  const [customResidentialConfig, setCustomResidentialConfig] = useState(null);
  const [customCommercialConfig, setCustomCommercialConfig] = useState(null);
  const [customSingleConfig, setCustomSingleConfig] = useState(null);
  
  // Waste generation data (needed for custom mode)
  const [wasteGeneration, setWasteGeneration] = useState(null);
  const [residentialWasteGen, setResidentialWasteGen] = useState(null);
  const [commercialWasteGen, setCommercialWasteGen] = useState(null);
  
  const [selectedMeasures, setSelectedMeasures] = useState([]);
  
  // ==================== WASTE CHUTE MODULE (START) ====================
  const [hasWasteChute, setHasWasteChute] = useState(false);
  const [chuteStreams, setChuteStreams] = useState([]);
  const [chuteDischarge, setChuteDischarge] = useState('');
  // ==================== WASTE CHUTE MODULE (END) ====================
  
  // ==================== BIN TUG MODULE (START) ====================
  const [hasBinTug, setHasBinTug] = useState(false);
  // ==================== BIN TUG MODULE (END) ====================
  
  // ==================== ACCESS & COLLECTION SUMMARY (START) ====================
  const [loadingZoneLocation, setLoadingZoneLocation] = useState('');
  const [residentialBinRoomLocation, setResidentialBinRoomLocation] = useState('');
  const [commercialBinRoomLocation, setCommercialBinRoomLocation] = useState('');
  const [bulkyWasteStorageLocation, setBulkyWasteStorageLocation] = useState('');
  const [wasteCollectedFrom, setWasteCollectedFrom] = useState('');
  const [accessAdditionalDetails, setAccessAdditionalDetails] = useState('');
  // ==================== ACCESS & COLLECTION SUMMARY (END) ====================
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Determine if this is a Medical Centre project (third stream = Clinical Waste)
  const isMedicalFacility = () => {
    const commercialOps = project?.step2_data?.commercial_operations || [];
    return commercialOps.some(op => {
      const opType = (op.operation_type || '').toLowerCase();
      return opType.includes('medical') || 
             opType.includes('health') ||
             opType.includes('hospital') ||
             opType.includes('healthcare');
    });
  };

  const getThirdStreamLabel = () => {
    return isMedicalFacility() ? "Clinical Waste" : "Organics / FOGO";
  };

  useEffect(() => {
    loadConfigurations();
  }, [project]);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await getBinConfigurations(project.id);
      
      if (response.is_mixed_use) {
        setIsMixedUse(true);
        setResidentialConfigurations(response.residential_configurations || []);
        setCommercialConfigurations(response.commercial_configurations || []);
        
        // Extract waste generation for custom mode
        if (response.residential_waste_generation) {
          setResidentialWasteGen(response.residential_waste_generation);
        } else if (response.residential_configurations?.[0]) {
          // Estimate from first config
          const cfg = response.residential_configurations[0];
          setResidentialWasteGen({
            general_waste: cfg.streams.general_waste.weekly_generation,
            recycling: cfg.streams.recycling.weekly_generation,
            organics: cfg.streams.organics.weekly_generation,
          });
        }
        
        if (response.commercial_waste_generation) {
          setCommercialWasteGen(response.commercial_waste_generation);
        } else if (response.commercial_configurations?.[0]) {
          const cfg = response.commercial_configurations[0];
          setCommercialWasteGen({
            general_waste: cfg.streams.general_waste.weekly_generation,
            recycling: cfg.streams.recycling.weekly_generation,
            organics: cfg.streams.organics.weekly_generation,
          });
        }
      } else {
        setIsMixedUse(false);
        setConfigurations(response.configurations || []);
        
        // Extract waste generation for custom mode
        if (response.waste_generation) {
          setWasteGeneration(response.waste_generation);
        } else if (response.configurations?.[0]) {
          const cfg = response.configurations[0];
          setWasteGeneration({
            general_waste: cfg.streams.general_waste.weekly_generation,
            recycling: cfg.streams.recycling.weekly_generation,
            organics: cfg.streams.organics.weekly_generation,
          });
        }
      }

      // Restore previous selections if available
      if (project.step5_data) {
        setSelectedMeasures(project.step5_data.waste_management_measures || []);
        
        // ==================== WASTE CHUTE MODULE (START) ====================
        // Restore waste chute selections
        setHasWasteChute(project.step5_data.has_waste_chute || false);
        setChuteStreams(project.step5_data.chute_streams || []);
        setChuteDischarge(project.step5_data.chute_discharge || '');
        // ==================== WASTE CHUTE MODULE (END) ====================
        
        // ==================== BIN TUG MODULE (START) ====================
        setHasBinTug(project.step5_data.has_bin_tug || false);
        // ==================== BIN TUG MODULE (END) ====================
        
        // ==================== ACCESS & COLLECTION SUMMARY (START) ====================
        setLoadingZoneLocation(project.step5_data.loading_zone_location || '');
        setResidentialBinRoomLocation(project.step5_data.residential_bin_room_location || '');
        setCommercialBinRoomLocation(project.step5_data.commercial_bin_room_location || '');
        setBulkyWasteStorageLocation(project.step5_data.bulky_waste_storage_location || '');
        setWasteCollectedFrom(project.step5_data.waste_collected_from || '');
        setAccessAdditionalDetails(project.step5_data.access_additional_details || '');
        // ==================== ACCESS & COLLECTION SUMMARY (END) ====================
        
        if (response.is_mixed_use) {
          // Check if custom mode was used
          if (project.step5_data.residential_custom_config) {
            setResidentialMode('custom');
            setCustomResidentialConfig(project.step5_data.residential_custom_config);
          } else {
            setSelectedResidentialConfig(project.step5_data.selected_residential_configuration || null);
          }
          
          if (project.step5_data.commercial_custom_config) {
            setCommercialMode('custom');
            setCustomCommercialConfig(project.step5_data.commercial_custom_config);
          } else {
            setSelectedCommercialConfig(project.step5_data.selected_commercial_configuration || null);
          }
        } else {
          if (project.step5_data.custom_bin_configuration) {
            setSingleMode('custom');
            setCustomSingleConfig(project.step5_data.custom_bin_configuration);
          } else {
            setSelectedConfig(project.step5_data.selected_bin_configuration || null);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load configurations:', err);
      setError('Failed to load bin configurations. Please go back and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigSelect = (optionNumber) => {
    setSelectedConfig(optionNumber);
  };

  const handleResidentialConfigSelect = (optionNumber) => {
    setSelectedResidentialConfig(optionNumber);
  };

  const handleCommercialConfigSelect = (optionNumber) => {
    setSelectedCommercialConfig(optionNumber);
  };

  const handleMeasureToggle = (measureId) => {
    setSelectedMeasures(prev => 
      prev.includes(measureId)
        ? prev.filter(id => id !== measureId)
        : [...prev, measureId]
    );
  };

  const handleSelectAllMeasures = () => {
    if (selectedMeasures.length === WASTE_MANAGEMENT_MEASURES.length) {
      setSelectedMeasures([]);
    } else {
      setSelectedMeasures(WASTE_MANAGEMENT_MEASURES.map(m => m.id));
    }
  };

  const isCustomConfigValid = (customConfig, wasteGen) => {
    if (!customConfig || !wasteGen) return false;
    const streams = customConfig.streams || {};
    return (
      (streams.general_waste?.weekly_capacity || 0) >= wasteGen.general_waste &&
      (streams.recycling?.weekly_capacity || 0) >= wasteGen.recycling &&
      (streams.organics?.weekly_capacity || 0) >= wasteGen.organics
    );
  };

  const canSubmit = () => {
    if (selectedMeasures.length < 3) return false;
    
    if (isMixedUse) {
      const resValid = residentialMode === 'auto' 
        ? selectedResidentialConfig !== null 
        : isCustomConfigValid(customResidentialConfig, residentialWasteGen);
      const comValid = commercialMode === 'auto'
        ? selectedCommercialConfig !== null
        : isCustomConfigValid(customCommercialConfig, commercialWasteGen);
      return resValid && comValid;
    } else {
      return singleMode === 'auto'
        ? selectedConfig !== null
        : isCustomConfigValid(customSingleConfig, wasteGeneration);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    try {
      setSaving(true);
      setError('');

      const step5Data = {
        waste_management_measures: selectedMeasures,
        // ==================== WASTE CHUTE MODULE (START) ====================
        has_waste_chute: hasWasteChute,
        chute_streams: hasWasteChute ? chuteStreams : null,
        chute_discharge: hasWasteChute ? chuteDischarge : null,
        // ==================== WASTE CHUTE MODULE (END) ====================
        // ==================== BIN TUG MODULE (START) ====================
        has_bin_tug: hasBinTug,
        // ==================== BIN TUG MODULE (END) ====================
        // ==================== ACCESS & COLLECTION SUMMARY (START) ====================
        loading_zone_location: loadingZoneLocation || null,
        residential_bin_room_location: residentialBinRoomLocation || null,
        commercial_bin_room_location: commercialBinRoomLocation || null,
        bulky_waste_storage_location: bulkyWasteStorageLocation || null,
        waste_collected_from: wasteCollectedFrom || null,
        access_additional_details: accessAdditionalDetails || null
        // ==================== ACCESS & COLLECTION SUMMARY (END) ====================
      };

      if (isMixedUse) {
        // Residential
        if (residentialMode === 'custom') {
          step5Data.residential_custom_config = customResidentialConfig;
          step5Data.selected_residential_configuration = null;
        } else {
          step5Data.selected_residential_configuration = selectedResidentialConfig;
          step5Data.residential_custom_config = null;
        }
        
        // Commercial
        if (commercialMode === 'custom') {
          step5Data.commercial_custom_config = customCommercialConfig;
          step5Data.selected_commercial_configuration = null;
        } else {
          step5Data.selected_commercial_configuration = selectedCommercialConfig;
          step5Data.commercial_custom_config = null;
        }
        
        // For backwards compatibility
        step5Data.selected_bin_configuration = residentialMode === 'auto' ? selectedResidentialConfig : null;
      } else {
        if (singleMode === 'custom') {
          step5Data.custom_bin_configuration = customSingleConfig;
          step5Data.selected_bin_configuration = null;
        } else {
          step5Data.selected_bin_configuration = selectedConfig;
          step5Data.custom_bin_configuration = null;
        }
      }

      await updateProjectStep5(project.id, step5Data);
      const updatedProject = await getProject(project.id);
      onComplete(updatedProject);
    } catch (err) {
      console.error('Failed to save Step 5:', err);
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Extract message from first validation error
        setError(detail[0]?.msg || 'Validation error. Please check your selections.');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading configurations...</p>
      </div>
    );
  }

  const hasConfigurations = isMixedUse 
    ? (residentialConfigurations.length > 0 || commercialConfigurations.length > 0)
    : configurations.length > 0;

  if (!hasConfigurations) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-amber-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Configurations Available</h2>
        <p className="text-gray-600 mb-4">Please complete the previous steps first.</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Custom Mix Feature Banner component
  const CustomMixBanner = ({ onCustomClick }) => (
    <div 
      data-testid="custom-mix-banner"
      className="mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 bg-amber-100 rounded-full p-2">
          <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-amber-800">Need a Different Bin Setup?</h4>
          <p className="text-xs text-amber-700 mt-1">
            Our pre-calculated options may not suit every project. Use <strong>Custom Mix</strong> to hand-pick your own combination of bin sizes and quantities to perfectly match your requirements.
          </p>
        </div>
        <button
          type="button"
          onClick={onCustomClick}
          data-testid="custom-mix-banner-btn"
          className="flex-shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-md transition-colors shadow-sm"
        >
          Try Custom Mix
        </button>
      </div>
    </div>
  );

  // Mode toggle component
  const ModeToggle = ({ mode, setMode, label }) => (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-sm text-gray-600">{label}:</span>
      <div className="inline-flex rounded-lg border border-gray-300 p-1">
        <button
          type="button"
          onClick={() => setMode('auto')}
          data-testid="mode-toggle-auto"
          className={`px-3 py-1 text-sm rounded-md transition-colors ${
            mode === 'auto' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Auto-Calculate
        </button>
        <button
          type="button"
          onClick={() => setMode('custom')}
          data-testid="mode-toggle-custom"
          className={`px-3 py-1 text-sm rounded-md transition-colors ${
            mode === 'custom' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Custom Mix
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Step 5: Waste Calculations</h2>
        <p className="mt-1 text-sm text-gray-600">
          {isMixedUse 
            ? 'Select your preferred bin configurations for both Residential and Commercial waste, then choose management measures.'
            : 'Select your preferred bin configuration and choose the waste management measures for your project.'
          }
        </p>
      </div>

      <div className="px-8 py-6 space-y-8">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Section 1: Bin Configuration Selection */}
        {isMixedUse ? (
          <>
            {/* Mixed-Use Info Banner */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-purple-800">Mixed-Use Development</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Select one configuration option for Residential bins and one for Commercial bins. 
                    You can choose pre-calculated options or create a custom mix of bin sizes.
                  </p>
                </div>
              </div>
            </div>

            {/* Residential Configuration Selection */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">1a. Select Residential Bin Configuration</h3>
                  <p className="text-sm text-gray-600">Council Collection • {project.collections_per_week || 1}x per week</p>
                </div>
              </div>
              
              {/* Custom Mix Highlight Banner - only show when in auto mode */}
              {residentialMode === 'auto' && (
                <CustomMixBanner onCustomClick={() => setResidentialMode('custom')} />
              )}
              
              <ModeToggle mode={residentialMode} setMode={setResidentialMode} label="Configuration mode" />
              
              {residentialMode === 'auto' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {residentialConfigurations.map((config, index) => (
                    <ConfigCard
                      key={`res-${index}`}
                      config={config}
                      optionNumber={index + 1}
                      isSelected={selectedResidentialConfig === index + 1}
                      onClick={() => handleResidentialConfigSelect(index + 1)}
                      label={`Residential Option ${index + 1}`}
                      thirdStreamLabel={getThirdStreamLabel()}
                    />
                  ))}
                </div>
              ) : (
                residentialWasteGen && (
                  <CustomBinConfigurator
                    wasteGeneration={residentialWasteGen}
                    collectionsPerWeek={project.collections_per_week || 1}
                    onConfigChange={setCustomResidentialConfig}
                    initialConfig={customResidentialConfig}
                    label="Custom Residential Bin Mix"
                    thirdStreamLabel={getThirdStreamLabel()}
                  />
                )
              )}
            </div>

            {/* Commercial Configuration Selection */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">1b. Select Commercial Bin Configuration</h3>
                  <p className="text-sm text-gray-600">Private Contractor • {project.commercial_collections_per_week || 1}x per week</p>
                </div>
              </div>
              
              {/* Custom Mix Highlight Banner - only show when in auto mode */}
              {commercialMode === 'auto' && (
                <CustomMixBanner onCustomClick={() => setCommercialMode('custom')} />
              )}
              
              <ModeToggle mode={commercialMode} setMode={setCommercialMode} label="Configuration mode" />
              
              {commercialMode === 'auto' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {commercialConfigurations.map((config, index) => (
                    <ConfigCard
                      key={`com-${index}`}
                      config={config}
                      optionNumber={index + 1}
                      isSelected={selectedCommercialConfig === index + 1}
                      onClick={() => handleCommercialConfigSelect(index + 1)}
                      label={`Commercial Option ${index + 1}`}
                      thirdStreamLabel={getThirdStreamLabel()}
                    />
                  ))}
                </div>
              ) : (
                commercialWasteGen && (
                  <CustomBinConfigurator
                    wasteGeneration={commercialWasteGen}
                    collectionsPerWeek={project.commercial_collections_per_week || 1}
                    onConfigChange={setCustomCommercialConfig}
                    initialConfig={customCommercialConfig}
                    label="Custom Commercial Bin Mix"
                    thirdStreamLabel={getThirdStreamLabel()}
                  />
                )
              )}
            </div>
          </>
        ) : (
          /* Non-Mixed-Use: Single Configuration Selection */
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              1. Select Your Preferred Bin Configuration
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose one of the pre-calculated options, or create a custom mix of bin sizes.
            </p>
            
            {/* Custom Mix Highlight Banner - only show when in auto mode */}
            {singleMode === 'auto' && (
              <CustomMixBanner onCustomClick={() => setSingleMode('custom')} />
            )}
            
            <ModeToggle mode={singleMode} setMode={setSingleMode} label="Configuration mode" />
            
            {singleMode === 'auto' ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {configurations.map((config, index) => (
                  <ConfigCard
                    key={index}
                    config={config}
                    optionNumber={index + 1}
                    isSelected={selectedConfig === index + 1}
                    onClick={() => handleConfigSelect(index + 1)}
                    thirdStreamLabel={getThirdStreamLabel()}
                  />
                ))}
              </div>
            ) : (
              wasteGeneration && (
                <CustomBinConfigurator
                  wasteGeneration={wasteGeneration}
                  collectionsPerWeek={project.collections_per_week || 1}
                  onConfigChange={setCustomSingleConfig}
                  initialConfig={customSingleConfig}
                  label="Custom Bin Mix"
                  thirdStreamLabel={getThirdStreamLabel()}
                />
              )
            )}
          </div>
        )}

        {/* Section 2: Waste Management Measures */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                2. Select Waste Management Measures
              </h3>
              <p className="text-sm text-gray-600">
                Select the measures that will be implemented in your project (minimum 3 required).
              </p>
            </div>
            <button
              type="button"
              onClick={handleSelectAllMeasures}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {selectedMeasures.length === WASTE_MANAGEMENT_MEASURES.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {WASTE_MANAGEMENT_MEASURES.map((measure) => {
              const isChecked = selectedMeasures.includes(measure.id);

              return (
                <label
                  key={measure.id}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-all ${
                    isChecked
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleMeasureToggle(measure.id)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${isChecked ? 'text-blue-900' : 'text-gray-900'}`}>
                      {measure.title}
                    </p>
                    <p className={`text-xs ${isChecked ? 'text-blue-700' : 'text-gray-500'}`}>
                      {measure.description}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Selection counter */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className={`${selectedMeasures.length >= 3 ? 'text-green-600' : 'text-amber-600'}`}>
              {selectedMeasures.length} of {WASTE_MANAGEMENT_MEASURES.length} measures selected
              {selectedMeasures.length < 3 && ' (minimum 3 required)'}
            </span>
            {selectedMeasures.length >= 3 && (
              <span className="text-green-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Minimum requirement met
              </span>
            )}
          </div>
        </div>

        {/* ==================== WASTE CHUTE MODULE (START) ==================== */}
        {/* Section 3: Waste Chute (Optional) */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            3. Waste Chute System (Optional)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            If your development includes a waste chute system, indicate this below. Final chute design is typically confirmed during detailed design.
          </p>
          
          {/* Main Toggle */}
          <div className="mb-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasWasteChute}
                onChange={(e) => {
                  setHasWasteChute(e.target.checked);
                  if (!e.target.checked) {
                    setChuteStreams([]);
                    setChuteDischarge('');
                  }
                }}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-900">
                Does the development include a waste chute system?
              </span>
            </label>
          </div>
          
          {/* Conditional Options (only shown if hasWasteChute is true) */}
          {hasWasteChute && (
            <div className="ml-8 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              {/* Waste Streams */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Waste streams served (optional)
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chuteStreams.includes('general_waste')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setChuteStreams([...chuteStreams, 'general_waste']);
                        } else {
                          setChuteStreams(chuteStreams.filter(s => s !== 'general_waste'));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">General Waste</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chuteStreams.includes('recycling')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setChuteStreams([...chuteStreams, 'recycling']);
                        } else {
                          setChuteStreams(chuteStreams.filter(s => s !== 'recycling'));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Recycling</span>
                  </label>
                </div>
              </div>
              
              {/* Chute Discharge Point */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chute discharge point (optional)
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="chuteDischarge"
                      value="bulk_bins"
                      checked={chuteDischarge === 'bulk_bins'}
                      onChange={(e) => setChuteDischarge(e.target.value)}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Bulk Bins</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="chuteDischarge"
                      value="compactor"
                      checked={chuteDischarge === 'compactor'}
                      onChange={(e) => setChuteDischarge(e.target.value)}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Compactor</span>
                  </label>
                </div>
              </div>
              
              {/* Helper Note */}
              <p className="text-xs text-gray-500 italic">
                Note: Final chute design, fire separation and ventilation details will be confirmed during detailed design in accordance with the NCC and relevant Australian Standards.
              </p>
            </div>
          )}
        </div>
        {/* ==================== WASTE CHUTE MODULE (END) ==================== */}

        {/* ==================== BIN TUG MODULE (START) ==================== */}
        {/* Section 4: Electric Bin Tug (Optional) */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            4. Electric Bin Tug / Movement Assistance (Optional)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            If your development requires assisted bin movement for heavy bins or long distances, indicate this below.
          </p>
          
          {/* Main Toggle */}
          <div className="mb-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasBinTug}
                onChange={(e) => setHasBinTug(e.target.checked)}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-900">
                Does the development require electric bin tug / movement assistance?
              </span>
            </label>
          </div>
          
          {/* Conditional Info (only shown if hasBinTug is true) */}
          {hasBinTug && (
            <div className="ml-8 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700">
                Enabling a single operator to safely move the heaviest bins, bin towing tugs improve workplace safety and eliminate manual handling.
              </p>
              <p className="text-sm text-gray-700">
                Offering a safer alternative to manual handling, bin tugs deliver the controlled movement of commercial bins inside, outside and on slopes - reducing the risk of injury and boosting productivity.
              </p>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  When selected, bin tug specifications will be included in Appendix F of your Waste Management Plan.
                </p>
              </div>
            </div>
          )}
        </div>
        {/* ==================== BIN TUG MODULE (END) ==================== */}

        {/* ==================== ACCESS & COLLECTION SUMMARY (START) ==================== */}
        {/* Section 5: Access & Collection Summary */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            5. Access & Collection Summary
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Provide details about bin storage locations and collection arrangements. Leave blank to enter custom text, or select "Not Applicable" if not relevant to your development.
          </p>
          
          <div className="space-y-4">
            {/* Loading Zone Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loading Zone or Collection Area Location
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={loadingZoneLocation}
                  onChange={(e) => setLoadingZoneLocation(e.target.value)}
                  placeholder="e.g., Ground floor loading dock accessed via rear laneway"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={loadingZoneLocation === 'Not Applicable'}
                />
                <button
                  type="button"
                  onClick={() => setLoadingZoneLocation(loadingZoneLocation === 'Not Applicable' ? '' : 'Not Applicable')}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    loadingZoneLocation === 'Not Applicable'
                      ? 'bg-gray-200 border-gray-400 text-gray-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>

            {/* Residential Bin Room Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Residential Bin Room Location
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={residentialBinRoomLocation}
                  onChange={(e) => setResidentialBinRoomLocation(e.target.value)}
                  placeholder="e.g., Basement Level 1, adjacent to car park entrance"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={residentialBinRoomLocation === 'Not Applicable'}
                />
                <button
                  type="button"
                  onClick={() => setResidentialBinRoomLocation(residentialBinRoomLocation === 'Not Applicable' ? '' : 'Not Applicable')}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    residentialBinRoomLocation === 'Not Applicable'
                      ? 'bg-gray-200 border-gray-400 text-gray-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>

            {/* Commercial Bin Room Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commercial Bin Room Location
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={commercialBinRoomLocation}
                  onChange={(e) => setCommercialBinRoomLocation(e.target.value)}
                  placeholder="e.g., Ground floor, rear of tenancy with direct laneway access"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={commercialBinRoomLocation === 'Not Applicable'}
                />
                <button
                  type="button"
                  onClick={() => setCommercialBinRoomLocation(commercialBinRoomLocation === 'Not Applicable' ? '' : 'Not Applicable')}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    commercialBinRoomLocation === 'Not Applicable'
                      ? 'bg-gray-200 border-gray-400 text-gray-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>

            {/* Bulky Waste Storage Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bulky Waste Storage Area Location
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={bulkyWasteStorageLocation}
                  onChange={(e) => setBulkyWasteStorageLocation(e.target.value)}
                  placeholder="e.g., Caged area within residential bin room"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={bulkyWasteStorageLocation === 'Not Applicable'}
                />
                <button
                  type="button"
                  onClick={() => setBulkyWasteStorageLocation(bulkyWasteStorageLocation === 'Not Applicable' ? '' : 'Not Applicable')}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    bulkyWasteStorageLocation === 'Not Applicable'
                      ? 'bg-gray-200 border-gray-400 text-gray-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>

            {/* Waste Collected From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Waste shall be collected from
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={wasteCollectedFrom}
                  onChange={(e) => setWasteCollectedFrom(e.target.value)}
                  placeholder="e.g., On-site loading dock / Street frontage / Rear laneway"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={wasteCollectedFrom === 'Not Applicable'}
                />
                <button
                  type="button"
                  onClick={() => setWasteCollectedFrom(wasteCollectedFrom === 'Not Applicable' ? '' : 'Not Applicable')}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    wasteCollectedFrom === 'Not Applicable'
                      ? 'bg-gray-200 border-gray-400 text-gray-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional details (if required)
              </label>
              <div className="flex gap-2">
                <textarea
                  value={accessAdditionalDetails}
                  onChange={(e) => setAccessAdditionalDetails(e.target.value)}
                  placeholder="Any additional access or collection information..."
                  rows={2}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={accessAdditionalDetails === 'Not Applicable'}
                />
                <button
                  type="button"
                  onClick={() => setAccessAdditionalDetails(accessAdditionalDetails === 'Not Applicable' ? '' : 'Not Applicable')}
                  className={`px-3 py-2 text-sm rounded-lg border self-start ${
                    accessAdditionalDetails === 'Not Applicable'
                      ? 'bg-gray-200 border-gray-400 text-gray-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* ==================== ACCESS & COLLECTION SUMMARY (END) ==================== */}

        {/* Summary Box */}
        {canSubmit() && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Your Selections Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {isMixedUse ? (
                <>
                  <div>
                    <span className="text-blue-700">Residential Config:</span>
                    <span className="ml-2 font-medium text-blue-900">
                      {residentialMode === 'custom' ? 'Custom Mix' : `Option ${selectedResidentialConfig}`}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700">Commercial Config:</span>
                    <span className="ml-2 font-medium text-blue-900">
                      {commercialMode === 'custom' ? 'Custom Mix' : `Option ${selectedCommercialConfig}`}
                    </span>
                  </div>
                </>
              ) : (
                <div>
                  <span className="text-blue-700">Bin Configuration:</span>
                  <span className="ml-2 font-medium text-blue-900">
                    {singleMode === 'custom' ? 'Custom Mix' : `Option ${selectedConfig}`}
                  </span>
                </div>
              )}
              <div>
                <span className="text-blue-700">Management Measures:</span>
                <span className="ml-2 font-medium text-blue-900">{selectedMeasures.length} selected</span>
              </div>
              {/* ==================== WASTE CHUTE MODULE (START) ==================== */}
              {hasWasteChute && (
                <div>
                  <span className="text-blue-700">Waste Chute:</span>
                  <span className="ml-2 font-medium text-blue-900">Yes</span>
                </div>
              )}
              {/* ==================== WASTE CHUTE MODULE (END) ==================== */}
              {/* ==================== BIN TUG MODULE (START) ==================== */}
              {hasBinTug && (
                <div>
                  <span className="text-blue-700">Bin Tug:</span>
                  <span className="ml-2 font-medium text-blue-900">Yes</span>
                </div>
              )}
              {/* ==================== BIN TUG MODULE (END) ==================== */}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !canSubmit()}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Continue to Refinements'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step5ManagementMeasures;
